import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { useMoveGroupToSpace } from 'APP/main/hooks/space/useMoveGroupToSpace';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Space } from 'STORE/Spaces/Space';

export interface MoveGroupToSpacePopupParams {
  groupId: string;
}

interface IMoveGroupToSpacePresenter {
  group: Group | null;
  spaces: Space[];
  onSelectSpace(spaceId: string): Promise<void>;
  onClose(): void;
}

export function useMoveGroupToSpacePresenter({
  popupInstance,
  params,
}: ICommonPopupsProps<MoveGroupToSpacePopupParams>): IMoveGroupToSpacePresenter {
  const { t } = useTranslation();
  const { groupId } = params!;
  const moveGroupToSpace = useMoveGroupToSpace(groupId);

  const presenter = useLocalStore<IMoveGroupToSpacePresenter>(() => ({
    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get spaces(): Space[] {
      return moveGroupToSpace.spaces;
    },

    async onSelectSpace(spaceId: string): Promise<void> {
      presenter.onClose();

      const canMoveGroup = await Tasks.space.confirmSpaceGroupsForMove(spaceId, groupId);

      if (!canMoveGroup) {
        return;
      }

      const success = await Tasks.space.moveGroupsToSpace(spaceId, [groupId]);

      success
        ? Entities.toast.show(t('space_alert_moved'))
        : Tasks.app.showAlert(t('common_somethings_wrong_error'));
    },

    onClose(): void {
      popupInstance.close();
    },
  }));

  useEffect(() => {
    if (!Entities.GroupsStore.isLoading && !presenter.group) {
      presenter.onClose();
    }
  }, [presenter.group, Entities.GroupsStore.isLoading]);

  return presenter;
}
